import { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Pen, Eraser, Undo2, Trash2, Type, Save, ZoomIn, ZoomOut, Circle } from "lucide-react";

interface Props {
  imageUrl: string;
  onSave: (dataUrl: string) => void;
  saving?: boolean;
}

type Tool = "pen" | "eraser" | "text" | "circle";

const COLORS = [
  "#ef4444", // red
  "#22c55e", // green
  "#3b82f6", // blue
  "#f59e0b", // amber
  "#000000", // black
  "#ffffff", // white
];

export default function ImageAnnotator({ imageUrl, onSave, saving }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState("#ef4444");
  const [lineWidth, setLineWidth] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState<ImageData[]>([]);
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [textMode, setTextMode] = useState(false);

  // Load background image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setBgImage(img);
      const canvas = canvasRef.current;
      if (!canvas || !containerRef.current) return;
      const containerWidth = containerRef.current.clientWidth;
      const ratio = containerWidth / img.width;
      const s = Math.min(ratio, 1);
      setScale(s);
      canvas.width = img.width * s;
      canvas.height = img.height * s;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      saveState();
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const saveState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setHistory((prev) => [...prev, data]);
  }, []);

  const redrawBg = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bgImage) return;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bgImage, 0, 0, canvas.width, canvas.height);
  }, [bgImage]);

  const undo = () => {
    const canvas = canvasRef.current;
    if (!canvas || history.length <= 1) return;
    const ctx = canvas.getContext("2d")!;
    const newHistory = [...history];
    newHistory.pop();
    const prev = newHistory[newHistory.length - 1];
    ctx.putImageData(prev, 0, 0);
    setHistory(newHistory);
  };

  const clearAll = () => {
    redrawBg();
    saveState();
  };

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (tool === "text") {
      const pos = getPos(e);
      const text = prompt("أدخل النص:");
      if (text) {
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext("2d")!;
        ctx.font = `bold ${lineWidth * 6}px sans-serif`;
        ctx.fillStyle = color;
        ctx.textAlign = "right";
        ctx.fillText(text, pos.x, pos.y);
        saveState();
      }
      return;
    }

    if (tool === "circle") {
      const pos = getPos(e);
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d")!;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, lineWidth * 8, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
      saveState();
      return;
    }

    setIsDrawing(true);
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = tool === "eraser" ? lineWidth * 4 : lineWidth;
    ctx.strokeStyle = tool === "eraser" ? "rgba(255,255,255,0)" : color;
    ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d")!;
      ctx.globalCompositeOperation = "source-over";
      saveState();
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Re-compose: draw bg + annotations at full resolution
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const ctx = exportCanvas.getContext("2d")!;
    ctx.drawImage(canvas, 0, 0);
    const dataUrl = exportCanvas.toDataURL("image/jpeg", 0.9);
    onSave(dataUrl);
  };

  const tools: { id: Tool; icon: any; label: string }[] = [
    { id: "pen", icon: Pen, label: "قلم" },
    { id: "eraser", icon: Eraser, label: "ممحاة" },
    { id: "text", icon: Type, label: "نص" },
    { id: "circle", icon: Circle, label: "دائرة" },
  ];

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-2 bg-muted rounded-lg">
        {tools.map((t) => (
          <Button
            key={t.id}
            variant={tool === t.id ? "default" : "ghost"}
            size="sm"
            onClick={() => setTool(t.id)}
            className="gap-1"
          >
            <t.icon className="w-4 h-4" />
            <span className="text-xs hidden sm:inline">{t.label}</span>
          </Button>
        ))}

        <div className="w-px h-6 bg-border mx-1" />

        {/* Colors */}
        <div className="flex items-center gap-1">
          {COLORS.map((c) => (
            <button
              key={c}
              className={`w-6 h-6 rounded-full border-2 transition-transform ${
                color === c ? "border-foreground scale-125" : "border-transparent"
              }`}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Line width */}
        <div className="flex items-center gap-2 min-w-[100px]">
          <span className="text-xs text-muted-foreground">سُمك</span>
          <Slider
            value={[lineWidth]}
            onValueChange={(v) => setLineWidth(v[0])}
            min={1}
            max={10}
            step={1}
            className="flex-1"
          />
        </div>

        <div className="w-px h-6 bg-border mx-1" />

        <Button variant="ghost" size="sm" onClick={undo} disabled={history.length <= 1}>
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={clearAll}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="border rounded-lg overflow-auto bg-muted max-h-[400px] flex items-center justify-center"
      >
        <canvas
          ref={canvasRef}
          className="cursor-crosshair touch-none"
          style={{ maxWidth: "100%", display: "block" }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>

      {/* Save */}
      <Button className="w-full" onClick={handleSave} disabled={saving}>
        <Save className="w-4 h-4 ml-2" />
        {saving ? "جاري الحفظ..." : "حفظ الصورة المُصحّحة"}
      </Button>
    </div>
  );
}
